import React, { useState, useRef, useEffect } from 'react';
import { Send, Trash2, Bot, User, Loader2 } from 'lucide-react';
import Button from './Button';

export default function ChatUI({
  messages = [],
  onSendMessage,
  onClear,
  placeholder = 'Type a message...',
  showClear = true,
  helperText,
  aiName = 'AI',
  disabled = false,
  headerRight = null,
  isClearing = false
}) {
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || disabled) return;
    
    const message = input.trim();
    setInput('');
    
    // Show typing indicator
    setIsTyping(true);
    
    await onSendMessage(message);
    
    setIsTyping(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format AI response text: convert *text* or **text** to bold, remove # headers, add proper line breaks
  const formatAIResponse = (text) => {
    if (!text) return null;

    // Remove markdown headers (# ## ### etc.) but keep the text
    let formatted = text.replace(/^#{1,6}\s*/gm, '');

    // Split by double newlines for paragraphs
    const paragraphs = formatted.split(/\n\n+/);

    return paragraphs.map((paragraph, pIndex) => {
      // Split paragraph by single newlines
      const lines = paragraph.split('\n');

      return (
        <p key={pIndex} className={pIndex > 0 ? 'mt-3' : ''}>
          {lines.map((line, lIndex) => {
            // Process bold text - handle both **text** and *text* patterns
            // First convert **text** to a placeholder, then handle *text*
            // This prevents **text** from being partially matched by *text* pattern

            // Split by **bold** first (double asterisks)
            const doubleBoldParts = line.split(/(\*\*[^*]+\*\*)/g);

            const processedLine = doubleBoldParts.map((part, partIndex) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                // Double asterisks - make bold
                return (
                  <strong key={`db-${partIndex}`} className="font-semibold">
                    {part.slice(2, -2)}
                  </strong>
                );
              }

              // Now handle single asterisks *text* in remaining text
              const singleBoldParts = part.split(/(\*[^*]+\*)/g);
              return singleBoldParts.map((subPart, subIndex) => {
                if (subPart.startsWith('*') && subPart.endsWith('*') && subPart.length > 2) {
                  // Single asterisks - make bold
                  return (
                    <strong key={`sb-${partIndex}-${subIndex}`} className="font-semibold">
                      {subPart.slice(1, -1)}
                    </strong>
                  );
                }
                return subPart;
              });
            });

            return (
              <span key={lIndex}>
                {lIndex > 0 && <br />}
                {processedLine}
              </span>
            );
          })}
        </p>
      );
    });
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 rounded-2xl border border-border overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-border">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-accent">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <span className="font-medium text-text-primary">{aiName}</span>
        </div>
        <div className="flex items-center gap-2">
          {headerRight}
          {showClear && messages.length > 0 && (
            <Button
              variant="ghost"
              size="small"
              icon={isClearing ? Loader2 : Trash2}
              onClick={onClear}
              disabled={isClearing}
              className={isClearing ? '[&>svg]:animate-spin' : ''}
            >
              {isClearing ? 'Clearing...' : 'Clear'}
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="p-4 rounded-2xl bg-primary/5 mb-4">
              <Bot className="w-10 h-10 text-primary/40" />
            </div>
            <p className="text-text-muted">
              {helperText || 'Send a message to start the conversation'}
            </p>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => (
              <div
                key={msg.id || index}
                className={`flex ${msg.type === 'user' ? 'justify-end pr-2' : 'justify-start'} animate-slide-up`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className={`flex items-end gap-2 max-w-[75%] ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`
                    flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
                    ${msg.type === 'user' ? 'bg-primary' : 'bg-gray-200'}
                  `}>
                    {msg.type === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-text-secondary" />
                    )}
                  </div>
                  <div>
                    <div className={`chat-bubble ${msg.type === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'}`}>
                      {msg.type === 'ai' ? formatAIResponse(msg.text) : msg.text}
                    </div>
                    {msg.time && (
                      <p className={`text-xs text-text-muted mt-1 ${msg.type === 'user' ? 'text-right' : ''}`}>
                        {formatTime(msg.time)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start animate-fade-in">
                <div className="flex items-end gap-2">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-text-secondary" />
                  </div>
                  <div className="chat-bubble chat-bubble-ai">
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-text-muted animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full bg-text-muted animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full bg-text-muted animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 bg-white border-t border-border">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isTyping}
            className="flex-1 px-4 py-3 bg-gray-50 rounded-xl border border-border
                       text-text-primary placeholder:text-text-muted
                       focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-200"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || disabled || isTyping}
            className="!p-3"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
